"use client";

import Image, { type ImageProps } from "next/image";
import { useSiteSettings } from "@/modules/web/hooks/useSiteSettings";

type Variant = "default" | "light";

type SiteLogoProps = Omit<ImageProps, "src" | "alt"> & {
  /**
   * "light" asks for the variant drawn for dark grounds (the footer). It falls
   * back to the default mark when a region has not uploaded one, which is what
   * every region does today.
   */
  variant?: Variant;
  /** Bundled mark to show until settings arrive, and if the region has none. */
  fallbackSrc: string;
  alt?: string;
};

/**
 * The site logo, resolved from the current region's settings.
 *
 * Every logo on the site used to be a hard-coded path, so a region could upload
 * its own mark and still see the Ijaazah one everywhere but the footer. This
 * reads `site.logo` / `site.logo_light` and keeps the bundled file as the
 * fallback.
 *
 * No `mounted` guard is needed to keep hydration honest: useSiteSettings is
 * seeded by SiteSettingsProvider with the settings the server already fetched,
 * so the server render and the client's first render produce the SAME mark —
 * the region's, not the bundled one. That seed is what removed the visible
 * swap from bundled logo to region logo on every cold load.
 *
 * `fallbackSrc` still matters: it covers a region with no uploaded mark, and
 * any tree rendered outside the provider.
 */
export default function SiteLogo({
  variant = "default",
  fallbackSrc,
  alt,
  ...imageProps
}: SiteLogoProps) {
  const settings = useSiteSettings();

  const configured =
    variant === "light"
      ? settings?.site.logoLight ?? settings?.site.logo
      : settings?.site.logo;

  return (
    <Image
      {...imageProps}
      src={configured ?? fallbackSrc}
      alt={alt ?? settings?.site.name ?? "logo"}
    />
  );
}
